import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Select, DatePicker, Popconfirm, message, Space, Tag, Card, Row, Col, Statistic, Tooltip, Tabs, Grid } from 'antd';
import { EditOutlined, SearchOutlined, FileTextOutlined, DeleteOutlined, LockOutlined, FilterOutlined } from '@ant-design/icons';
import { islemlerAPI, carilerAPI, kasalarAPI, kullanicilarAPI } from '../services/api';
import { useLookups } from '../hooks/useLookups';
import { formatPara, paraInputProps } from '../utils/format';
import { bosSecenekliOptions, secimeUyuyor } from '../utils/filtre';
import dayjs from 'dayjs';
import StokHareketleriTab from './StokHareketleriTab';

const ISLEM_TIPLERI = {
  1: { label: 'Satis', renk: 'green' },
  2: { label: 'Alis', renk: 'blue' },
  3: { label: 'Satis Iade', renk: 'orange' },
  4: { label: 'Alis Iade', renk: 'orange' },
  9: { label: 'Odeme', renk: 'red' },
  10: { label: 'Tahsilat', renk: 'cyan' },
};

function LogKayitlari() {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const [data, setData] = useState([]);
  const [cariler, setCariler] = useState([]);
  const [kasalar, setKasalar] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [aramaMetni, setAramaMetni] = useState('');
  const [seciliTipler, setSeciliTipler] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [form] = Form.useForm();

  const { kdvOptions: kdvOranlari } = useLookups();

  useEffect(() => {
    try {
      const kullanici = JSON.parse(localStorage.getItem('kullanici') || '{}');
      setIsAdmin(kullanici.rol === 'admin');
    } catch { setIsAdmin(false); }
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [islemlerRes, carilerRes, kasalarRes] = await Promise.all([
        islemlerAPI.getAll(),
        carilerAPI.getAll(),
        kasalarAPI.getAll(),
      ]);
      setData(islemlerRes.data);
      setCariler(carilerRes.data);
      setKasalar(kasalarRes.data);
    } catch (err) {
      message.error('Log kayitlari yuklenemedi: ' + (err.response?.data?.hata || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSubmit = async (values) => {
    if (!isAdmin) {
      message.error('Bu islemi sadece admin kullanicilar yapabilir');
      return;
    }
    const payload = {
      ...values,
      tarih: values.tarih?.format('YYYY-MM-DD'),
    };
    try {
      await islemlerAPI.update(editingRecord.id, payload);
      message.success('Kayit guncellendi');
      setModalOpen(false);
      setEditingRecord(null);
      form.resetFields();
      fetchData();
    } catch (err) {
      message.error('Guncellenemedi: ' + (err.response?.data?.hata || err.message));
    }
  };

  const handleEdit = (record) => {
    if (!isAdmin) {
      message.warning('Duzenleme yetkisi sadece admin kullanicilara aittir');
      return;
    }
    setEditingRecord(record);
    form.setFieldsValue({
      cari_id: record.cari_id,
      kasa_id: record.kasa_id,
      tutar: record.tutar,
      kdv_orani_id: record.kdv_orani_id,
      aciklama: record.aciklama,
      tarih: record.tarih ? dayjs(record.tarih) : null,
    });
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!isAdmin) {
      message.warning('Silme yetkisi sadece admin kullanicilara aittir');
      return;
    }
    try {
      await islemlerAPI.remove(id);
      message.success('Kayit silindi');
      fetchData();
    } catch (err) {
      message.error('Silinemedi: ' + (err.response?.data?.hata || err.message));
    }
  };

  const filteredData = data.filter(d => {
    const aramaUygun = !aramaMetni ||
      (d.cari || '').toLowerCase().includes(aramaMetni.toLowerCase()) ||
      (d.aciklama || '').toLowerCase().includes(aramaMetni.toLowerCase()) ||
      (d.kasa_ad || '').toLowerCase().includes(aramaMetni.toLowerCase());
    const tipUygun = secimeUyuyor(seciliTipler, d.islem_tipi_id);
    return aramaUygun && tipUygun;
  });

  const toplamTutar = filteredData.reduce((t, d) => t + (Number(d.toplam_tutar) || 0), 0);
  const kayitSayisi = filteredData.length;

  const tipFiltresi = Object.entries(ISLEM_TIPLERI).map(([key, val]) => ({
    value: Number(key),
    label: val.label,
  }));
  const tipFiltreSecenekleri = bosSecenekliOptions(tipFiltresi, data, 'islem_tipi_id');

  const columns = [
    {
      title: '#',
      key: 'sira',
      width: 50,
      align: 'center',
      responsive: ['lg'],
      render: (_, __, index) => <span style={{ color: '#999', fontSize: 12 }}>{index + 1}</span>,
    },
    {
      title: 'Tarih',
      dataIndex: 'tarih',
      key: 'tarih',
      width: isMobile ? 80 : 110,
      render: (v) => v ? dayjs(v).format('DD.MM.YYYY') : '-',
      sorter: (a, b) => { const d = new Date(a.tarih || 0) - new Date(b.tarih || 0); return d !== 0 ? d : (a.id || 0) - (b.id || 0); },
      defaultSortOrder: 'descend',
    },
    {
      title: 'Islem Tipi',
      dataIndex: 'islem_tipi_id',
      key: 'islem_tipi_id',
      width: 120,
      render: (v) => {
        const tip = ISLEM_TIPLERI[v];
        return tip ? <Tag color={tip.renk}>{tip.label}</Tag> : <Tag>{v || '-'}</Tag>;
      },
      filters: tipFiltresi.map(t => ({ text: t.label, value: t.value })),
      onFilter: (value, record) => record.islem_tipi_id === value,
    },
    {
      title: 'Cari',
      dataIndex: 'cari',
      key: 'cari',
      ellipsis: true,
      responsive: ['sm'],
      sorter: (a, b) => (a.cari || '').localeCompare(b.cari || '', 'tr'),
    },
    {
      title: 'Kasa',
      dataIndex: 'kasa_ad',
      key: 'kasa_ad',
      width: 130,
      responsive: ['md'],
      sorter: (a, b) => (a.kasa_ad || '').localeCompare(b.kasa_ad || '', 'tr'),
    },
    {
      title: 'Tutar',
      dataIndex: 'tutar',
      key: 'tutar',
      width: 120,
      align: 'right',
      responsive: ['md'],
      render: (v) => v != null ? formatPara(v) : '-',
      sorter: (a, b) => (a.tutar || 0) - (b.tutar || 0),
    },
    {
      title: 'KDV',
      dataIndex: 'kdv_orani',
      key: 'kdv_orani',
      width: 70,
      align: 'center',
      responsive: ['lg'],
      render: (v) => v != null ? `%${Number(v)}` : '-',
    },
    {
      title: 'Toplam',
      dataIndex: 'toplam_tutar',
      key: 'toplam_tutar',
      width: isMobile ? 90 : 120,
      align: 'right',
      render: (v) => v != null ? <strong>{formatPara(v)}</strong> : '-',
      sorter: (a, b) => (a.toplam_tutar || 0) - (b.toplam_tutar || 0),
    },
    {
      title: 'Aciklama',
      dataIndex: 'aciklama',
      key: 'aciklama',
      ellipsis: true,
      responsive: ['lg'],
      render: (v) => v || <span style={{ color: '#ccc' }}>-</span>,
    },
    {
      title: 'Islem',
      key: 'islem',
      width: 140,
      render: (_, record) => isAdmin ? (
        <Space>
          <Tooltip title="Duzenle">
            <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          </Tooltip>
          <Popconfirm title="Bu kaydi silmek istediginize emin misiniz?" onConfirm={() => handleDelete(record.id)} okText="Evet" cancelText="Hayir">
            <Tooltip title="Sil">
              <Button danger size="small" icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ) : (
        <Tooltip title="Duzenleme icin admin yetkisi gereklidir">
          <LockOutlined style={{ color: '#ccc' }} />
        </Tooltip>
      ),
    },
  ];

  const finansalTab = (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'nowrap' }}>
        <Space size={6} style={{ flexShrink: 0 }}>
          <Select
            mode="multiple"
            allowClear
            placeholder={isMobile ? 'Tip' : 'Islem Tipi Filtrele'}
            style={{ minWidth: isMobile ? 100 : 220 }}
            options={tipFiltreSecenekleri}
            value={seciliTipler}
            onChange={(val) => setSeciliTipler(val)}
            maxTagCount="responsive"
            suffixIcon={<FilterOutlined />}
          />
          <Input
            placeholder="Ara..."
            prefix={<SearchOutlined />}
            allowClear
            onChange={(e) => setAramaMetni(e.target.value)}
            style={{ width: isMobile ? 110 : 220 }}
          />
        </Space>
      </div>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Card size="small" style={{ borderLeft: '3px solid #FF6B00' }}>
            <Statistic title="Toplam Kayit" value={kayitSayisi} />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small" style={{ borderLeft: '3px solid #52c41a' }}>
            <Statistic title="Toplam Tutar" value={toplamTutar} precision={2} suffix="TL" />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small" style={{ borderLeft: '3px solid #1890ff' }}>
            <Statistic title="Islem Tipi Sayisi" value={[...new Set(filteredData.map(d => d.islem_tipi_id))].length} />
          </Card>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={filteredData}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={{
          defaultPageSize: 20,
          pageSizeOptions: ['10', '20', '50', '100'],
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kayit`,
        }}
        rowClassName={(record, index) => index % 2 === 0 ? '' : 'ant-table-row-light'}
        style={{ fontSize: 13 }}
      />

      <Modal
        title="Kayit Duzenle"
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setEditingRecord(null); form.resetFields(); }}
        onOk={() => form.submit()}
        width={480}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="tarih" label="Tarih" rules={[{ required: true }]} initialValue={dayjs()}>
            <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="cari_id" label="Cari" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="label" placeholder="Cari secin" options={cariler.map(c => ({ value: c.id, label: c.ad }))} />
          </Form.Item>
          <Form.Item name="kasa_id" label="Kasa" rules={[{ required: true, message: 'Kasa secimi zorunludur' }]}>
            <Select showSearch optionFilterProp="label" placeholder="Kasa secin" options={kasalar.filter(k => k.aktif).map(k => ({ value: k.id, label: k.ad }))} />
          </Form.Item>
          <Form.Item name="tutar" label="Tutar (TL)" rules={[{ required: true }]}>
            <InputNumber {...paraInputProps} min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="kdv_orani_id" label="KDV Orani" rules={[{ required: true }]}>
            <Select options={kdvOranlari} placeholder="KDV orani secin" />
          </Form.Item>
          <Form.Item name="aciklama" label="Aciklama">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <FileTextOutlined style={{ fontSize: 24, color: '#FF6B00' }} />
        <h2 style={{ margin: 0 }}>Log Kayitlari</h2>
        {!isAdmin && (
          <Tag icon={<LockOutlined />} color="default" style={{ marginLeft: 8 }}>Salt Okunur</Tag>
        )}
        {isAdmin && (
          <Tag color="green" style={{ marginLeft: 8 }}>Admin</Tag>
        )}
      </div>
      <Tabs
        defaultActiveKey="finansal"
        items={[
          { key: 'finansal', label: <span style={{ display: 'inline-block', whiteSpace: 'pre-line', lineHeight: 1.2, textAlign: 'center' }}>{'Finansal İşlem\nLogları'}</span>, children: finansalTab },
          { key: 'stok', label: <span style={{ display: 'inline-block', whiteSpace: 'pre-line', lineHeight: 1.2, textAlign: 'center' }}>{'Stok Hareketleri\nLogları'}</span>, children: <StokHareketleriTab /> },
        ]}
      />
    </div>
  );
}

export default LogKayitlari;
